import { useEffect, useState, useSyncExternalStore } from 'react';
import {
  api, getUser, isAuthenticated, logout, restore, subscribe, switchTenant,
  type TenantOption,
} from './lib/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Login } from './routes/Login';
import { Sites } from './routes/Sites';
import { Team } from './routes/Team';
import { AcceptInvite } from './routes/AcceptInvite';
import { SiteDetail, type Tab } from './routes/SiteDetail';
import { Badge, Button, Mark } from './lib/ui';
import { roleHint, roleLabel } from './lib/labels';
import { HelpPage } from './features/help/Help';
import { getLang, setLang, t, type Lang } from './lib/i18n';

/**
 * Routing is a hash and a switch.
 *
 * Doc 04 §1.3 picks TanStack Router for typed search params, which earns its keep once
 * the dashboard's date range, filters and cursors live in the URL. None of that exists
 * yet, so this is the smaller thing that works; swap it in when there is state worth
 * putting in a query string.
 */
function useHashRoute(): string {
  const [hash, setHash] = useState(() => location.hash.slice(1) || '/sites');
  useEffect(() => {
    const onChange = (): void => setHash(location.hash.slice(1) || '/sites');
    addEventListener('hashchange', onChange);
    return () => removeEventListener('hashchange', onChange);
  }, []);
  return hash;
}

export const navigate = (to: string): void => {
  location.hash = to;
};

export function App() {
  const authed = useSyncExternalStore(subscribe, isAuthenticated);
  const [booting, setBooting] = useState(true);
  const route = useHashRoute();

  // A reload should not mean a login: the HttpOnly refresh cookie survives, so try a
  // silent refresh before deciding the user is logged out.
  useEffect(() => {
    void restore().finally(() => setBooting(false));
  }, []);

  // Accepting an invitation happens before there is an account, so it is checked before
  // the auth gate rather than behind it.
  const invite = /^\/einladung\?token=(.+)$/.exec(route);
  if (invite) {
    return <AcceptInvite token={decodeURIComponent(invite[1]!)} onDone={() => navigate('/sites')} />;
  }

  if (booting) {
    // A bare ellipsis on a blank page tells nobody whether the app is loading or broken.
    return (
      <div className="grid min-h-screen place-items-center">
        <div className="flex items-center gap-2 text-sm text-[var(--color-muted)]">
          <Mark size={18} />
          {t('Sitzung wird geprüft …')}
        </div>
      </div>
    );
  }
  if (!authed) return <Login onDone={() => navigate('/sites')} />;

  const siteMatch = /^\/sites\/([^/]+?)(?:\/(stats|banner|blocking|scan|proofs|settings))?$/.exec(route);
  const onTeam = route.startsWith('/team');
  // A second segment is the topic. Deep links matter here: the dialog's "all topics" link
  // and every ? marker point at one.
  const helpMatch = /^\/hilfe(?:\/([a-z0-9-]+))?$/.exec(route);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-[1560px] px-6 py-7">
        {helpMatch ? <HelpPage slug={helpMatch[1]} />
          : onTeam ? <Team />
          : siteMatch ? (
            <SiteDetail
              siteId={siteMatch[1]!}
              tab={(siteMatch[2] as Tab) ?? 'stats'}
              onTab={(t) => navigate(`/sites/${siteMatch[1]}/${t}`)}
            />
          )
          : <Sites />}
      </main>
    </div>
  );
}

/**
 * Which account this is, and how to get to the others.
 *
 * Only a switch when there is something to switch to: someone in one tenant — which is
 * most customers — should not be given a control that does nothing. The role sits beside
 * it rather than in it, because the role is per tenant: the same person can be owner of
 * their own account and editor of a client's.
 */
function TenantPicker({ current }: { current: string }) {
  const tenants = useQuery({
    queryKey: ['tenants'],
    queryFn: () => api<{ data: TenantOption[] }>('/v1/auth/tenants'),
    staleTime: 5 * 60 * 1000,
  });
  const qc = useQueryClient();
  const list = tenants.data?.data ?? [];

  if (list.length < 2) return <div className="font-medium">{current}</div>;

  return (
    <select
      className="-ml-1 max-w-[220px] cursor-pointer rounded-[3px] border-0 bg-transparent px-1 py-0
                 text-[13px] font-medium outline-none transition-colors hover:bg-[var(--color-sunken)]"
      aria-label={t('Konto wechseln')}
      value={list.find((t) => t.name === current)?.id ?? ''}
      onChange={(e) => {
        void (async () => {
          await switchTenant(e.target.value);
          // Every cached list is keyed by ids that mean nothing in the next account, and
          // showing the previous customer's sites for even one frame is not acceptable
          // on a product whose whole promise is separation.
          qc.clear();
          navigate('/sites');
        })();
      }}
    >
      {list.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
    </select>
  );
}

function Header() {
  // Subscribed, not read once. Switching accounts replaces the session object, and
  // isAuthenticated() — the only thing App subscribes to — stays true through it, so
  // nothing re-rendered and the header kept naming the account you had just left.
  const user = useSyncExternalStore(subscribe, getUser);
  return (
    <header className="border-b border-[var(--color-line)] bg-[var(--color-surface)]">
      <div className="mx-auto flex max-w-[1560px] flex-wrap items-center justify-between gap-x-4 gap-y-2 px-6 py-2.5">
        <a href="#/sites" className="flex items-center gap-2 text-[var(--color-ink)]">
          <Mark />
          <span className="text-[13px] font-semibold tracking-tight">Consent</span>
          <span className="text-[13px] text-[var(--color-muted)]">Console</span>
        </a>
        {/* Three groups, not one run-on line. It used to read
            "Team owner@demo.test | Demo GmbH tenant_owner Abmelden": navigation, identity
            and the way out, all at the same weight, with the role printed as a database
            value. */}
        <div className="flex items-center gap-4 text-[13px]">
          <a href="#/team"
            className="rounded-[3px] px-2 py-1 text-[var(--color-muted)] transition-colors
                       hover:bg-[var(--color-sunken)] hover:text-[var(--color-ink)]">
            {t('Team')}
          </a>
          <a href="#/hilfe"
            className="rounded-[3px] px-2 py-1 text-[var(--color-muted)] transition-colors
                       hover:bg-[var(--color-sunken)] hover:text-[var(--color-ink)]">
            {t('Hilfe')}
          </a>
          <div className="flex items-center gap-2 border-l border-[var(--color-line)] pl-4">
            {/* The tenant decides what every screen here is about, so it is set in ink. The
                address below it is the least important thing and the most likely to be long. */}
            <div className="leading-tight">
              <TenantPicker current={user?.tenant ?? ''} />
              <div className="hidden text-[11px] text-[var(--color-muted)] md:block">{user?.email}</div>
            </div>
            <Badge title={t('Ihre Rolle: {hint}', {
              hint: roleHint(user?.role) ?? t('bestimmt, was Sie hier tun können'),
            })}>
              {roleLabel(user?.role)}
            </Badge>
          </div>
          <LangPicker />
          <Button variant="ghost" size="sm" onClick={() => void logout()}>{t('Abmelden')}</Button>
        </div>
      </div>
    </header>
  );
}

/**
 * Two languages, in the header, next to the way out.
 *
 * Deliberately not in the banner editor's toolbar: switching remounts the tree and drops
 * the undo history with it, so the control belongs where nobody reaches for it mid-edit.
 */
function LangPicker() {
  return (
    <select
      className="cursor-pointer rounded-[3px] border-0 bg-transparent px-1 py-0 text-[13px]
                 text-[var(--color-muted)] outline-none transition-colors
                 hover:bg-[var(--color-sunken)] hover:text-[var(--color-ink)]"
      aria-label={t('Sprache')}
      value={getLang()}
      onChange={(e) => setLang(e.target.value as Lang)}
    >
      <option value="de">{t('Deutsch')}</option>
      <option value="en">{t('Englisch')}</option>
    </select>
  );
}
